import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  Menu,
  message,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
} from "antd";
import { arrayRemove, arrayUnion, doc, updateDoc } from "firebase/firestore";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Flex from "components/shared-components/Flex";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import _ from "lodash";
import roles from "roles";
import utils from "../../../../utils";
import {
  changeUserPassword,
  deleteUser as deleteUserApi,
  getFirebaseIDToken,
} from "../../../../functions/firebase/firebase";
import { checkMfaStatus, resetUserMfa } from "../../../../functions/api/mfa";
import { FIRESTORE_CUSTOMERS_TABLE, FIRESTORE_USERS_TABLE } from "../../../../constants/FirestoreConstant";
import { firestore } from "../../../../configs/FirebaseConfig";
import "firebase/compat/auth";
import { getUserStatus } from "../../../../functions/formatters";
import dayjs from "dayjs";
import styled from "styled-components";
import { isMobile } from "../../../../functions/browser";
import { inviteUser } from "../../../../functions/api/mail";
import { getPermissionStructure, getUserPermissions, saveUserPermissions } from "../../../../utils/permissionUtils";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import Loading from "../../../../components/shared-components/Loading";
import { useCmsUsers } from "../../../../data/hooks/firestore/useCmsUsers";
import { useWebshopUsers } from "../../../../data/hooks/firestore/useWebshopUsers";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import AddUserModal from "./modals/AddUserModal";
import EditUserModal from "./modals/EditUserModal";
import EditPasswordModal from "./modals/EditPasswordModal";
import PermissionsModal from "./modals/PermissionsModal";
import MfaModal from "./modals/MfaModal";

const { Text } = Typography;

const Admins = () => {
  const { customers: companies, isLoading: companiesLoading } = useCustomers();
  const { uid, profile, role: currentUserRole, email } = useAuth();
  const { cmsUsers, isLoading: cmsUsersLoading } = useCmsUsers();
  const { webshopUsers, isLoading: webshopUsersLoading } = useWebshopUsers();
  const [addUserForm] = Form.useForm();
  const [users, setUsers] = useState(cmsUsers);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [userEditForm] = Form.useForm();
  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(null);
  const [user, setUser] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userEditModalVisible, setUserEditModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editPasswordForm] = Form.useForm();
  const [editPasswordModalVisible, setEditPasswordModalVisible] = useState(false);

  // Permission-related state
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [editablePermissions, setEditablePermissions] = useState({});

  // MFA-related state
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(false);
  const [mfaRequiredLoading, setMfaRequiredLoading] = useState(false);
  const [mfaModalVisible, setMfaModalVisible] = useState(false);
  const [editableMfaRequired, setEditableMfaRequired] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (!!cmsUsers) {
      setUsers(cmsUsers);
      setFilteredUsers([]);
    }
  }, [cmsUsers]);

  // Load user permissions when drawer opens
  const loadUserPermissions = useCallback(
    async (selectedUser) => {
      // Only load permissions if current user is superAdmin
      if (currentUserRole !== roles.superAdmin) {
        return;
      }

      if (!selectedUser || selectedUser.userRole === roles.superAdmin) {
        setUserPermissions({});
        return;
      }

      setPermissionsLoading(true);
      try {
        const permissions = await getUserPermissions(selectedUser.id, selectedUser.userRole);
        setUserPermissions(permissions || {});
      } catch (error) {
        console.error("Error loading user permissions:", error);
        message.error(t("notifications.failed_to_load_user_permissions"));
      } finally {
        setPermissionsLoading(false);
      }
    },
    [currentUserRole],
  );

  // Load user MFA status when drawer opens
  const loadMfaStatus = useCallback(
    async (selectedUser) => {
      // Only load MFA status if current user is superAdmin
      if (currentUserRole !== roles.superAdmin) {
        return;
      }

      if (!selectedUser || selectedUser.userRole === roles.superAdmin) {
        setMfaStatus(null);
        return;
      }

      // Only check MFA status for admin users
      if (selectedUser.userRole !== roles.admin) {
        setMfaStatus(null);
        return;
      }

      setMfaStatusLoading(true);
      try {
        const status = await checkMfaStatus(selectedUser.id);
        setMfaStatus(status);
      } catch (error) {
        console.error("Error loading MFA status:", error);
        setMfaStatus(null);
      } finally {
        setMfaStatusLoading(false);
      }
    },
    [currentUserRole],
  );

  // Open permissions modal
  const openPermissionsModal = () => {
    setEditablePermissions({ ...userPermissions });
    setPermissionsModalVisible(true);
  };

  // Close permissions modal
  const closePermissionsModal = () => {
    setPermissionsModalVisible(false);
    setEditablePermissions({});
  };

  // Open MFA modal
  const openMfaModal = () => {
    setEditableMfaRequired(user?.mfaRequired ?? false);
    setMfaModalVisible(true);
  };

  // Close MFA modal
  const closeMfaModal = () => {
    setMfaModalVisible(false);
    setEditableMfaRequired(false);
  };

  const showDrawer = async (row) => {
    setDrawerVisible(true);
    setUser(row);
    await Promise.all([loadUserPermissions(row), loadMfaStatus(row)]);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setUser(null);
    setUserPermissions({});
    setMfaStatus(null);
  };

  // Handle permission change with auto-dependency logic
  const handlePermissionChange = (permissionKey, field, checked) => {
    setEditablePermissions((prev) => {
      const updated = { ...prev };

      if (field === "edit" && checked) {
        updated[permissionKey] = { view: true, edit: true };
      } else if (field === "view" && !checked) {
        updated[permissionKey] = { view: false, edit: false };
      } else {
        updated[permissionKey] = {
          ...prev[permissionKey],
          [field]: checked,
        };
      }

      return updated;
    });
  };

  // Save user permissions
  const handleSavePermissions = async () => {
    if (!user) return;

    setPermissionsLoading(true);
    try {
      const success = await saveUserPermissions(user.id, editablePermissions);
      if (success) {
        message.success(t("notifications.permissions_updated_successfully"));
        setUserPermissions(editablePermissions);
        closePermissionsModal();
      } else {
        message.error(t("notifications.failed_to_update_permissions"));
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      message.error(t("notifications.failed_to_save_permissions"));
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleToggleMfaRequired = async (required) => {
    if (!user) return;

    setMfaRequiredLoading(true);
    try {
      await updateDoc(doc(firestore, FIRESTORE_USERS_TABLE, user.id), {
        mfaRequired: required,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // Update local user state
      setUser({ ...user, mfaRequired: required });
      setEditableMfaRequired(required);

      message.success(
        required ? t("notifications.mfa_requirement_enabled") : t("notifications.mfa_requirement_disabled"),
      );
    } catch (error) {
      console.error("Error updating MFA requirement:", error);
      message.error(t("notifications.failed_to_update_mfa_requirement"));
    } finally {
      setMfaRequiredLoading(false);
    }
  };

  // Reset user MFA
  const handleResetMfa = async () => {
    if (!user) return;

    setMfaLoading(true);
    try {
      const response = await resetUserMfa(user.id);

      if (response.success) {
        message.success(t("notifications.mfa_reset_successfully"));
        // Refresh MFA status to show updated status
        await loadMfaStatus(user);
      } else {
        message.error(t("notifications.failed_to_reset_mfa"));
      }
    } catch (error) {
      console.error("Error resetting MFA:", error);
      message.error(error.response?.data?.message || t("notifications.failed_to_reset_mfa"));
    } finally {
      setMfaLoading(false);
    }
  };

  const handleEditPassword = () => {
    editPasswordForm.validateFields().then((values) => {
      setIsLoading(true);
      getFirebaseIDToken()
        .then((token) => {
          const object = {
            userId: userToEdit.id,
            password: values.password,
          };
          changeUserPassword(object, token)
            .then(() => {
              setEditPasswordModalVisible(false);
              setIsLoading(false);
              editPasswordForm.resetFields();
              setUserToEdit(null);
              message.success(t("notifications.password_changed_successfully"));
            })
            .catch((err) => {
              console.log(err);
              message.error(err.response?.data?.message || t("notifications.password_change_failed"));
              setIsLoading(false);
            });
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
          message.error(t("notifications.authentication_failed"));
        });
    });
  };

  const closeEditModal = () => {
    setUserToEdit(null);
    setUserEditModalVisible(false);
    userEditForm.resetFields();
  };

  const addUser = () => {
    setAddUserModalVisible(true);
  };

  const closeAddModal = () => {
    setAddUserModalVisible(false);
    addUserForm.resetFields();
  };

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
      <Menu.Item onClick={() => onPressEditUser(row)}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit")}</span>
        </Flex>
      </Menu.Item>
      {currentUserRole === roles.superAdmin && (
        <Menu.Item
          onClick={() => {
            setUserToEdit(row);
            setEditPasswordModalVisible(true);
          }}
        >
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>{t("edit_password")}</span>
          </Flex>
        </Menu.Item>
      )}
      {row.id !== uid && (
        <Menu.Item disabled={profile.userRole !== roles.superAdmin}>
          <Flex alignItems="center">
            <DeleteOutlined />
            <Popconfirm
              placement="bottom"
              title={t("confirm_delete_user", { email: row.userEmail })}
              onConfirm={() => deleteUser(row)}
              okText={t("yes")}
              cancelText={t("no")}
              disabled={profile.userRole !== roles.superAdmin}
            >
              <span style={{ marginLeft: 4 }}>{t("delete")}</span>
            </Popconfirm>
          </Flex>
        </Menu.Item>
      )}
    </Menu>
  );

  const onPressEditUser = (user) => {
    userEditForm.setFieldsValue({
      userFirstName: user.userFirstName,
      userLastName: user.userLastName,
      userPhoneNumber: user.userPhoneNumber,
      userCustomerId: user.userCustomerId,
      userRegion: user.userRegion,
    });

    setUserToEdit(user);
    setUserEditModalVisible(true);
  };

  const handleEditUser = () => {
    userEditForm.validateFields().then(async (values) => {
      const customerObject = companies.find((item) => item.customerId === values.userCustomerId);
      if (!!customerObject) {
        values.userCustomerName = customerObject.customerName;
        values.updatedAt = dayjs().unix();
        values.updatedBy = email;
        setIsLoading(true);
        try {
          await updateDoc(doc(firestore, FIRESTORE_USERS_TABLE, userToEdit.id), values);
          closeEditModal();
          closeDrawer();
          if (userToEdit.userCustomerId !== values.userCustomerId) {
            await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, userToEdit.userCustomerId), {
              customerUsers: arrayRemove(userToEdit.id),
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
            await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, values.userCustomerId), {
              customerUsers: arrayUnion(userToEdit.id),
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  const deleteUser = async (user) => {
    try {
      await deleteUserApi(user.id);
      message.success(t("notifications.user_deleted"), 2);
    } catch (error) {
      console.error("Error deleting user:", error);
      message.error(t("notifications.user_delete_failed"));
    }
  };

  const formatUserRole = (role) => {
    switch (role) {
      case roles.admin:
        return t("admin");
      case roles.superAdmin:
        return t("super_admin");
      default:
        return t("user");
    }
  };

  const handleInviteUser = () => {
    addUserForm.validateFields().then((values) => {
      values.userEmail = values.userEmail.toLowerCase();
      let cmsUserExists = _.find(cmsUsers, ["userEmail", values.userEmail]);
      let webshopUserExists = _.find(webshopUsers, ["userEmail", values.userEmail]);
      values.userPhoneNumber = values.userPhoneNumber || "";

      if (!!cmsUserExists) {
        message.error({ content: t("user_already_exists"), duration: 4 });
      } else if (!!webshopUserExists) {
        message.error({
          content: t("user_already_exists_webshop"),
          duration: 6,
        });
      } else {
        setIsLoading(true);

        // Flag to set up permissions for new admin users
        const object = {
          userEmail: values.userEmail,
          userFirstName: values.userFirstName,
          userLastName: values.userLastName,
          userPhoneNumber: values.userPhoneNumber,
          userRole: values.userRole,
          userRegion: values.userRegion,
          userStatus: "active",
          userEmailVerified: true,
          setupPermissions: values.userRole === roles.admin, // Flag to set up permissions with default false values
          isCmsUser: true,
        };

        getFirebaseIDToken()
          .then((token) => {
            inviteUser(object, token, true)
              .then(() => {
                setIsLoading(false);
                addUserForm.resetFields();
                closeAddModal();
                message.success(t("user_received_email"));
              })
              .catch((err) => {
                console.log("err: ", err);
                setIsLoading(false);
                message.error(t("user_invite_email_failed"));
              });
          })
          .catch((err) => {
            console.log("err: ", err);
            setIsLoading(false);
            message.error(err.message);
          });
      }
    });
  };

  // Render read-only permission section in drawer
  const renderPermissionSection = () => {
    if (!user) return null;

    if (currentUserRole !== roles.superAdmin) {
      return null;
    }

    if (user.userRole === roles.superAdmin) {
      return (
        <div>
          <Text type="secondary" style={{ fontStyle: "italic" }}>
            <KeyOutlined /> {t("user_is_a_super_user")}
          </Text>
        </div>
      );
    }

    if (user.userRole !== roles.admin) {
      return (
        <div>
          <Text type="secondary">{t("permission_management_only_for_admin")}</Text>
        </div>
      );
    }

    const permissionStructure = getPermissionStructure();

    // Check if user has any active permissions
    const hasAnyPermissions = Object.values(userPermissions).some((perm) => perm.view || perm.edit);

    if (!hasAnyPermissions) {
      return (
        <div>
          <Text type="secondary" style={{ fontStyle: "italic" }}>
            {t("no_active_permissions")}
          </Text>
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: 16, maxHeight: "300px", overflowY: "auto" }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            {permissionStructure.map((group) => {
              // Check if parent or any children have active permissions
              const parentPermission = userPermissions[group.key] || { view: false, edit: false };
              const hasParentPermission = parentPermission.view || parentPermission.edit;
              const hasChildPermissions = group.children.some((child) => {
                const childPerm = userPermissions[child.key] || { view: false, edit: false };
                return childPerm.view || childPerm.edit;
              });

              // Skip this group if no permissions are active
              if (!hasParentPermission && !hasChildPermissions) {
                return null;
              }

              return (
                <div key={group.key} style={{ marginBottom: "16px" }}>
                  {/* Parent header */}
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      marginBottom: "8px",
                      backgroundColor: "rgba(0,0,0,0.02)",
                      padding: "8px",
                    }}
                  >
                    {t(group.title)}
                  </div>

                  {/* Parent permissions (if it has a path and active permissions) */}
                  {group.hasPath && hasParentPermission && (
                    <div
                      style={{
                        padding: "8px 0 8px 16px",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Text>
                        {parentPermission.view && t("view")}
                        {parentPermission.view && parentPermission.edit && ", "}
                        {parentPermission.edit && t("edit")}
                      </Text>
                    </div>
                  )}

                  {/* Children - only show if they have active permissions */}
                  {group.children.map((child) => {
                    const permission = userPermissions[child.key] || { view: false, edit: false };

                    // Skip if no active permissions
                    if (!permission.view && !permission.edit) {
                      return null;
                    }

                    return (
                      <div
                        key={child.key}
                        style={{
                          padding: "8px 0 8px 32px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <div style={{ marginBottom: "4px" }}>
                          <Text strong>{t(child.title)}: </Text>
                          <Text>
                            {permission.view && t("view")}
                            {permission.view && permission.edit && ", "}
                            {permission.edit && t("edit")}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </Space>
        </div>
      </div>
    );
  };

  // Render read-only MFA section in drawer
  const renderMfaSection = () => {
    if (!user) return null;

    // Only superAdmins can manage MFA
    if (currentUserRole !== roles.superAdmin) {
      return null;
    }

    if (user.userRole === roles.superAdmin) {
      return (
        <div>
          <Text type="secondary" style={{ fontStyle: "italic" }}>
            <KeyOutlined /> {t("mfa_management_not_available_for_super_admin")}
          </Text>
        </div>
      );
    }

    if (user.userRole !== roles.admin) {
      return (
        <div>
          <Text type="secondary">{t("mfa_management_only_for_admin")}</Text>
        </div>
      );
    }

    // Show loading state while fetching MFA status
    if (mfaStatusLoading) {
      return (
        <div style={{ padding: "16px", textAlign: "center" }}>
          <Text type="secondary">{t("loading_mfa_status")}</Text>
        </div>
      );
    }

    // Use API data if available, fallback to user object properties
    const mfaEnabled = mfaStatus?.totpEnabled ?? user.totpEnabled ?? false;
    const mfaSetupDate = mfaStatus?.totpSetupDate ?? user.totpSetupDate;
    const lastUsed = mfaStatus?.lastUsed;
    const mfaRequired = user.mfaRequired ?? false;

    return (
      <div>
        <Space direction="vertical" style={{ width: "100%" }} size={0}>
          {/* MFA Requirement Status */}
          <div style={{ padding: "8px 0" }}>
            <Text strong>{t("mfa_requirement")}: </Text>
            <Text type={"secondary"}>{mfaRequired ? <>{t("required")}</> : <>{t("optional")}</>}</Text>
          </div>

          {/* MFA Status */}
          <div style={{ padding: "8px 0" }}>
            <Text strong>{t("mfa_status")}: </Text>
            <Text type={"secondary"}>
              {mfaEnabled ? (
                <>
                  <CheckOutlined style={{ color: "green", marginRight: 4 }} />
                  {t("enabled")}
                </>
              ) : (
                <>
                  <CloseOutlined style={{ color: "red", marginRight: 4 }} />
                  {t("disabled")}
                </>
              )}
            </Text>
          </div>

          {mfaEnabled && mfaSetupDate && (
            <div style={{ padding: "8px 0" }}>
              <Text strong>{t("setup_date")}: </Text>
              <Text type="secondary">
                {dayjs(mfaSetupDate.toDate ? mfaSetupDate.toDate() : mfaSetupDate).format("D MMM YYYY HH:mm")}
              </Text>
            </div>
          )}

          {mfaEnabled && lastUsed && (
            <div style={{ padding: "8px 0" }}>
              <Text strong>{t("last_used")}: </Text>
              <Text type="secondary">
                {dayjs(lastUsed.toDate ? lastUsed.toDate() : lastUsed).format("D MMM YYYY HH:mm")}
              </Text>
            </div>
          )}

          {!mfaEnabled && (
            <div style={{ padding: "8px 0" }}>
              <Text type="secondary" style={{ fontStyle: "italic" }}>
                {t("mfa_not_setup_by_user_yet")}
              </Text>
            </div>
          )}

          {mfaRequired && !mfaEnabled && (
            <div style={{ padding: "8px 0" }}>
              <Text type="warning" style={{ fontSize: "12px" }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                {t("user_must_setup_mfa_on_next_login")}
              </Text>
            </div>
          )}
        </Space>
      </div>
    );
  };

  const tableColumns = [
    {
      title: t("user_name"),
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          <span>{!!data.userFirstName ? data.userFirstName + " " + data.userLastName : data.userId}</span>
        </button>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "userEmail"),
      defaultSortOrder: "descend",
    },
    {
      title: t("email"),
      dataIndex: "userEmail",
      render: (data) => <span>{data}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "userEmail"),
    },
    {
      title: t("region"),
      dataIndex: "userRegion",
      sorter: (a, b) => utils.antdTableSorter(a, b, "userRegion"),
      render: (region) => <span>{region === "all" ? t("all") : region}</span>,
    },
    {
      title: t("role"),
      dataIndex: "userRole",
      render: (role) => <span>{formatUserRole(role)}</span>,
    },
    ...(currentUserRole === roles.superAdmin
      ? [
          {
            title: t("mfa_enabled"),
            dataIndex: "totpEnabled",
            render: (totpEnabled) => (
              <span>
                {totpEnabled ? (
                  <CheckOutlined style={{ color: "green" }} />
                ) : (
                  <CloseOutlined style={{ color: "red" }} />
                )}
              </span>
            ),
          },
        ]
      : []),
    {
      title: "",
      dataIndex: "actions",
      render: (_, row) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(row)} />
        </div>
      ),
    },
  ];

  const onSearch = (e) => {
    const value = e.currentTarget.value;
    if (value.length === 0) {
      setFilteredUsers([]); // Reset to empty so table shows original users
    } else {
      const data = utils.wildCardSearchOnUser(cmsUsers, value);
      setFilteredUsers(data);
    }
  };

  if (companiesLoading || cmsUsersLoading || webshopUsersLoading) {
    return <Loading />;
  }

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("admin_users")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Flex className="mb-4" alignItems="center" justifyContent="between" mobileFlex={false}>
              <Space wrap>
                <Input placeholder={t("search")} prefix={<SearchOutlined />} onChange={(e) => onSearch(e)} />
              </Space>
              <Space wrap>
                <Button onClick={addUser} type="primary" icon={<PlusCircleOutlined />} block>
                  {t("add_user")}
                </Button>
              </Space>
            </Flex>
            <div className="table-responsive">
              <Table
                showSorterTooltip={false}
                columns={tableColumns}
                dataSource={filteredUsers.length > 0 ? filteredUsers : users}
                rowKey="userId"
              />
            </div>

            <EditUserModal
              visible={userEditModalVisible}
              onCancel={closeEditModal}
              onOk={handleEditUser}
              form={userEditForm}
              userToEdit={userToEdit}
              companies={companies}
            />

            {!!user && (
              <Drawer
                title={t("user_data", { user: !!user ? user.userFirstName + " " + user.userLastName : "-" })}
                placement="right"
                onClose={closeDrawer}
                open={drawerVisible}
                width={isMobile ? window.innerWidth : 850}
              >
                <Descriptions
                  bordered
                  title={<div className="flex-row">{t("user_data", { user: "" })}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("user.user")}>
                    {user.userFirstName + " " + user.userLastName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.id")}>
                    {currentUserRole === roles.superAdmin ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const win = window.open("", "_blank");
                          win.document.write(`<pre>${JSON.stringify(user, null, 2)}</pre>`);
                          win.document.close();
                        }}
                        style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
                      >
                        {user.userId}
                      </a>
                    ) : (
                      user.userId
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.status")}>{getUserStatus(user.userStatus)}</Descriptions.Item>
                  <Descriptions.Item label={t("user.company")}>{user.userCustomerName}</Descriptions.Item>
                  <Descriptions.Item label={t("user.email")}>{user.userEmail}</Descriptions.Item>
                  <Descriptions.Item label={t("user.phone")}>{user.userPhoneNumber}</Descriptions.Item>
                  <Descriptions.Item label={t("region")}>
                    {t("user.region", { region: user.userRegion })}
                  </Descriptions.Item>
                </Descriptions>

                <ButtonsWrapper>
                  <Button onClick={() => onPressEditUser(user)}>{t("edit_data")}</Button>
                </ButtonsWrapper>

                {/* User Permissions Section - Only show for superAdmins */}
                {currentUserRole === roles.superAdmin && (
                  <>
                    <Divider />
                    <Descriptions
                      bordered
                      title={<div className="flex-row mt-4">{t("user_permissions")}</div>}
                      size="small"
                      column={1}
                    >
                      <Descriptions.Item label={t("permissions_management")}>
                        {renderPermissionSection()}
                      </Descriptions.Item>
                    </Descriptions>

                    <ButtonsWrapper>
                      <Button onClick={openPermissionsModal}>{t("update_permissions")}</Button>
                    </ButtonsWrapper>
                  </>
                )}

                {/* MFA Settings Section - Only show for superAdmins */}
                {currentUserRole === roles.superAdmin && (
                  <>
                    <Divider />
                    <Descriptions
                      bordered
                      title={<div className="flex-row mt-4">{t("mfa_settings")}</div>}
                      size="small"
                      column={1}
                    >
                      <Descriptions.Item label={t("multi_factor_authentication")}>
                        {renderMfaSection()}
                      </Descriptions.Item>
                    </Descriptions>

                    <ButtonsWrapper>
                      <Button onClick={openMfaModal}>{t("manage_mfa")}</Button>
                    </ButtonsWrapper>
                  </>
                )}

                <Divider />
                <Descriptions
                  bordered
                  title={<div className="flex-row mt-4">{t("user.app_and_device_data")}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("user.app_version")}>{user.userAppVersion}</Descriptions.Item>
                  <Descriptions.Item label={t("user.device")}>
                    {user.userDevice + " (" + user.userDeviceVersion + ")"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("verified")}>
                    {user.userEmailVerified ? t("yes") : t("no")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.receive_notifications")}>
                    {user.userReceivePushNotifications ? t("yes") : t("no")}
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions
                  bordered
                  title={<div className="flex-row mt-4">{t("user.user_actions_data")}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("user.registration_date")}>
                    {!!user.userRegistrationDate
                      ? dayjs(user.userRegistrationDate, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")
                      : "-"}
                  </Descriptions.Item>

                  <Descriptions.Item label={t("user.shopping_cart_items")}>
                    {user.userShoppingCartNrOfItems}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.last_update")}>
                    {!!user.userLastUpdate
                      ? Date(user.userLastUpdate).split("GMT")[0].toLocaleString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        })
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.last_action")}>
                    {!!user.userLastAction ? user.userLastAction : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("user.last_order")}>
                    {!!user.userLastOrderDate
                      ? dayjs(user.userLastOrderDate, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Drawer>
            )}

            <AddUserModal
              visible={addUserModalVisible}
              onCancel={closeAddModal}
              onOk={handleInviteUser}
              form={addUserForm}
              formEmail={formEmail}
              setFormEmail={setFormEmail}
              formPhone={formPhone}
              setFormPhone={setFormPhone}
              formFirstName={formFirstName}
              setFormFirstName={setFormFirstName}
              formLastName={formLastName}
              setFormLastName={setFormLastName}
              isLoading={isLoading}
            />

            <EditPasswordModal
              visible={editPasswordModalVisible}
              onCancel={() => {
                setUserToEdit(null);
                setEditPasswordModalVisible(false);
                editPasswordForm.resetFields();
              }}
              onOk={handleEditPassword}
              form={editPasswordForm}
              userToEdit={userToEdit}
              isLoading={isLoading}
            />

            <PermissionsModal
              visible={permissionsModalVisible}
              onCancel={closePermissionsModal}
              onOk={handleSavePermissions}
              editablePermissions={editablePermissions}
              handlePermissionChange={handlePermissionChange}
              permissionsLoading={permissionsLoading}
            />

            <MfaModal
              visible={mfaModalVisible}
              onCancel={closeMfaModal}
              user={user}
              mfaStatus={mfaStatus}
              mfaStatusLoading={mfaStatusLoading}
              editableMfaRequired={editableMfaRequired}
              handleToggleMfaRequired={handleToggleMfaRequired}
              mfaRequiredLoading={mfaRequiredLoading}
              handleResetMfa={handleResetMfa}
              mfaLoading={mfaLoading}
              loadMfaStatus={loadMfaStatus}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

export default Admins;
